import { useCallback, useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Button, FormControlLabel, Switch } from '@material-ui/core';
import { isEmpty } from 'lodash';

import AdminPage from '../AdminPage';
import { AdminPages, buildCreatePageTarget, buildPageId } from '../AdminLinkUtils';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import DataTable from '../../data-table/DataTable';
import { useQueryLoader } from '../../data-table/Loaders';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { Actions } from '../../page-framework';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import { useDataService } from '../../../services/ApiDataService';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { useGenericError } from '../../../services/FormErrorService';
import { deleteSelectedMaintenanceWindows } from './MaintenanceWindowsUtils';
import { MAINTENANCE_WINDOW_LIST_COLUMNS } from './MaintenanceWindowsConstants';
import useUiToasts from '../../../stores/ToastStore';
import Link from '../../links/Link';

const functionName = AdminPages.MaintenanceWindows;

const initialSort = [{ property: 'startTime', direction: SORT_ASC }];
const dataSource = {
    url: '/api/v3/maintenance-windows/list',
    totalProperty: 'total',
    dataProperty: 'data',
    params: {
        searchFields: ['name', 'description', 'startTime', 'endTime', 'sites', 'businessServices'],
    },
};

export default function MaintenanceWindowsList() {
    const [pendingOnly, setPendingOnly] = useState(true);
    const [actionsDisabled, setActionsDisabled] = useState(false);
    const [tableSelection, setTableSelection] = useState(undefined);
    const tableRowsRef = useRef(null);
    const [, setTableState] = useState(undefined);
    const loader = useQueryLoader(dataSource, { disableFilterState: true });
    const dataService = useDataService();
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const handleGenericError = useGenericError();
    const [, uiToastActions] = useUiToasts();

    useEffect(() => {
        dataSource.params = {
            ...dataSource.params,
            pendingOnly,
        };

        refresher();
    }, [pendingOnly, refresher]);

    const handleTableSelectionChange = useCallback((currentTableSelection, tableRows) => {
        tableRowsRef.current = tableRows;
        const disableActions =
            !currentTableSelection || (isEmpty(currentTableSelection?.selection) && !currentTableSelection.inverted);

        setTableSelection(currentTableSelection);
        setActionsDisabled(disableActions);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        let maintenanceWindowIds = [];
        if (tableSelection?.inverted) {
            maintenanceWindowIds = Object.values(tableRowsRef.current || {}).map((item) => item?.row?._id);
        } else {
            maintenanceWindowIds = tableSelection?.selection ?? [];
        }
        deleteSelectedMaintenanceWindows(
            maintenanceWindowIds,
            dataService,
            refresher,
            handleGenericError,
            uiToastActions
        );
    }, [tableSelection, dataService, uiToastActions, refresher, handleGenericError]);

    return (
        <AdminPage
            title={t`Maintenance Windows`}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <FormControlLabel
                        control={
                            <Switch
                                onChange={() => {
                                    setPendingOnly(!pendingOnly);
                                }}
                                checked={!pendingOnly}
                            />
                        }
                        label={t`Display past windows`}
                    />
                    <ConfirmDeleteButton
                        disabled={actionsDisabled}
                        handleConfirmedDelete={handleConfirmDelete}
                        plural={tableSelection?.selection?.length > 1}
                    />
                    <Button
                        component={Link}
                        to={buildCreatePageTarget(functionName)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Create</Trans>
                    </Button>
                </Actions>
            }
        >
            <DataTable
                tableId="admin-maintenance-windows-list"
                loader={loader}
                columns={MAINTENANCE_WINDOW_LIST_COLUMNS}
                triggerRef={triggerRef}
                visibleRows={0}
                showSelectionColumn
                sorters={initialSort}
                onStateChanged={setTableState}
                selectionOptions={{
                    mode: SELECTIONMODE_MULTI,
                    allowSelectAll: true,
                    idMapping: '_id',
                    onChange: handleTableSelectionChange,
                }}
            />
        </AdminPage>
    );
}
